import React, { useEffect, forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from 'components/common/IconButton';

import FormularioGastoFields from './FormularioGastoFields';
import DetallesCalculos from './DetallesCalculos';
import useGastoAPI from './useGastoAPI';
import useGastoData from './useGastoData';

const ModalRegistroGastoRefactor = ({
    setGastos,
    gastos,
    setSelectedGasto,
    selectedGasto,
    setUltimoGasto,
    ultimoGasto,
    copia,
    setCopia,
    Editar,
    setEditar,
    showModalNuevo,
    showModalRegistrarGasto,
    setShowModalRegistrarGasto
}) => {
    const { handleSubmit, reset } = useForm();
    const [guardando, setGuardando] = useState(false);
    // Internal modal loading state to avoid interfering with parent list state
    const [modalCargando, setModalCargando] = useState(false);

    // Custom hooks
    const {
        formasdePago,
        cuentasBancarias,
        responsableUsuario,
        tipoDeGasto,
        registrarGasto,
        inicializarDatos
    } = useGastoAPI();

    const {
        formData,
        setFormData,
        formErrors,
        setFormErrors,
        date,
        setDate,
        notaDeVenta,
        setNotaDeVenta,
        tipoDeCambio,
        setTipoDeCambio,
        tipoDeCambioTexto,
        handleChange,
        handleDateChange,
        obtenerTipoDeCambio,
        validarDatosGasto,
        seleccionarArchivoNotaPago,
        prepararDatosGasto
    } = useGastoData(selectedGasto, ultimoGasto);

    const opcionesDescripcion = Array.from(new Set(gastos.map(gasto => gasto.vDescripcion)));

    // Inicializa datos al montar
    useEffect(() => {
        obtenerTipoDeCambio();
        inicializarDatos();
    }, []);

    // Maneja copia de gasto
    useEffect(() => {
        if (copia && selectedGasto && Object.keys(selectedGasto).length > 0) {
            const fechaOriginal = selectedGasto.dFechaGasto ? new Date(selectedGasto.dFechaGasto) : new Date();
            setFormData({
                ...selectedGasto,
                idGasto: 0,
                dFechaGasto: fechaOriginal
            });
            setDate(fechaOriginal);
        }
    }, [copia, selectedGasto]);

    /**
     * Custom input for DatePicker
     */
    const GreetingsDateInput = forwardRef(({ value, onClick }, ref) => (
        <div className="position-relative">
            <Form.Control
                size="sm"
                ref={ref}
                onClick={onClick}
                value={value || ""}
                placeholder="Seleccione una fecha"
                className="ps-4"
                readOnly
            />
            <FontAwesomeIcon
                icon="calendar-alt"
                className="text-primary position-absolute top-50 translate-middle-y ms-2"
            />
        </div>
    ));

    GreetingsDateInput.propTypes = {
        value: PropTypes.string,
        onClick: PropTypes.func,
    };

    /**
     * Maneja el env�o del formulario
     */
    const onSubmit = async () => {
        if (!validarDatosGasto()) {
            return;
        }

        const datosGasto = prepararDatosGasto();

        const callbacks = {
            setGuardando,
            setEditar,
            setCargando: setModalCargando, // use modal-local setter
            setSelectedGasto,
            setShowModalRegistrarGasto,
            setGastos,
            setUltimoGasto
        };

        await registrarGasto(datosGasto, callbacks);
    };

    /**
     * Maneja el cierre del modal
     */
    const handleClose = () => {
        if (Editar && copia) {
            setShowModalRegistrarGasto(false);
            setSelectedGasto([]);
            setEditar(false);
            setCopia(false);
        } else if (Editar) {
            setEditar(false);
            setDate(null);
            setShowModalRegistrarGasto(false);
            reset();
            setSelectedGasto({});
        } else {
            setSelectedGasto({});
            setShowModalRegistrarGasto(false);
        }
    };

    return (
        <Modal
            show={showModalRegistrarGasto}
            size="lg"
            backdrop="static"
            centered
            keyboard={false}
            onHide={handleClose}
        >
            <Modal.Header>
                {showModalNuevo ? (
                    <Modal.Title>Registrar Gasto</Modal.Title>
                ) : (
                    <Modal.Title>{copia ? 'Copia Gasto' : 'Gasto'}</Modal.Title>
                )}
                <Button className="btn-close" onClick={handleClose} />
            </Modal.Header>

            <Form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Body>
                    <Row className="mb-2">
                        <FormularioGastoFields
                            showModalNuevo={showModalNuevo}
                            Editar={Editar}
                            cargando={modalCargando} // use internal modal loading state
                            formData={formData}
                            formErrors={formErrors}
                            handleChange={handleChange}
                            opcionesDescripcion={opcionesDescripcion}
                            tipoDeGasto={tipoDeGasto}
                            cuentasBancarias={cuentasBancarias}
                            responsableUsuario={responsableUsuario}
                            formasdePago={formasdePago}
                            selectedGasto={selectedGasto}
                            date={date}
                            handleDateChange={handleDateChange}
                            setFormErrors={setFormErrors}
                            GreetingsDateInput={GreetingsDateInput}
                            notaDeVenta={notaDeVenta}
                            seleccionarArchivoNotaPago={seleccionarArchivoNotaPago}
                            setNotaDeVenta={setNotaDeVenta}
                        />

                        <DetallesCalculos
                            showModalNuevo={showModalNuevo}
                            Editar={Editar}
                            cargando={modalCargando}
                            formData={formData}
                            formErrors={formErrors}
                            handleChange={handleChange}
                            selectedGasto={selectedGasto}
                            tipoDeCambio={tipoDeCambio}
                            setTipoDeCambio={setTipoDeCambio}
                            tipoDeCambioTexto={tipoDeCambioTexto}
                        />
                    </Row>
                </Modal.Body>

                <Modal.Footer>
                    {showModalNuevo ? (
                        /**
                         * Footer para el nuevo gasto
                         */
                        <>
                            <Col className="col-auto">
                                {guardando ? (
                                    <IconButton
                                        variant="falcon-default"
                                        size="sm"
                                        transform="shrink-2"
                                        className="d-sm-block me-2"
                                        disabled
                                    >
                                        <Spinner className="text-inline" animation="border" size="sm" variant="light" />&nbsp; Cargando...
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        variant="falcon-default"
                                        size="sm"
                                        icon="check"
                                        transform="shrink-2"
                                        className="d-sm-block me-2"
                                        type="submit"
                                    >
                                        <span className="ms-1">Guardar</span>
                                    </IconButton>
                                )}
                            </Col>
                            <IconButton
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleClose();
                                }}
                                variant="falcon-danger"
                                size="sm"
                                transform="shrink-2"
                                className="d-sm-block me-2"
                            >
                                <span className="ms-1">X Cancelar</span>
                            </IconButton>
                        </>
                    ) : (
                        /**
                         * Footer para gasto existente (editar o ver)
                         */
                        <>
                            {Editar ? (
                                /**
                                 * Footer en modo edici�n
                                 */
                                <>
                                    <Col className="col-auto">
                                        <IconButton
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (Editar && copia) {
                                                    setShowModalRegistrarGasto(false);
                                                    setSelectedGasto([]);
                                                    setEditar(false);
                                                    setCopia(false);
                                                } else if (Editar) {
                                                    setEditar(false);
                                                    reset();
                                                }
                                            }}
                                            variant="falcon-danger"
                                            size="sm"
                                            transform="shrink-2"
                                            className="d-sm-block me-2"
                                        >
                                            <span className="d-none d-xl-inline-block ms-1">X Cancelar</span>
                                        </IconButton>
                                    </Col>

                                    <Col className="col-auto">
                                        {guardando ? (
                                            <IconButton
                                                variant="falcon-default"
                                                size="sm"
                                                transform="shrink-2"
                                                className="d-sm-block me-2"
                                                disabled
                                            >
                                                <Spinner className="text-inline" animation="border" size="sm" variant="light" />&nbsp; Cargando...
                                            </IconButton>
                                        ) : (
                                            <IconButton
                                                variant="falcon-default"
                                                size="sm"
                                                icon="check"
                                                transform="shrink-2"
                                                className="d-sm-block me-2"
                                                type="submit"
                                            >
                                                <span className="d-none d-xl-inline-block ms-1">
                                                    {copia ? 'Guardar Copia' : 'Guardar'}
                                                </span>
                                            </IconButton>
                                        )}
                                    </Col>
                                </>
                            ) : (
                                /**
                                 * Footer en modo solo lectura
                                 */
                                <>
                                    <Col className="col-auto">
                                        <IconButton
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleClose();
                                            }}
                                            variant="falcon-danger"
                                            size="sm"
                                            transform="shrink-2"
                                            className="d-sm-block me-2"
                                        >
                                            <span className="d-none d-xl-inline-block ms-1">X Cancelar</span>
                                        </IconButton>
                                    </Col>
                                    <Col className="col-auto">
                                        <IconButton
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setEditar(true);
                                            }}
                                            variant="falcon-default"
                                            size="sm"
                                            icon="pencil-alt"
                                            transform="shrink-2"
                                            className="d-sm-block me-2"
                                        >
                                            <span className="d-none d-xl-inline-block ms-1">Editar</span>
                                        </IconButton>
                                    </Col>
                                </>
                            )}
                        </>
                    )}
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

ModalRegistroGastoRefactor.propTypes = {
    selectedGasto: PropTypes.object,
    setSelectedGasto: PropTypes.func,
    setUltimoGasto: PropTypes.func,
    ultimoGasto: PropTypes.object,
    setCopia: PropTypes.func,
    copia: PropTypes.bool,
    setEditar: PropTypes.func,
    Editar: PropTypes.bool,
    setGastos: PropTypes.func,
    gastos: PropTypes.array,
    showModalNuevo: PropTypes.bool,
    showModalRegistrarGasto: PropTypes.bool,
    setShowModalRegistrarGasto: PropTypes.func
};

export default ModalRegistroGastoRefactor;
